import { Checkbox, CheckboxGroup } from "@design-system/Checkbox/Checkbox";
import { Radio, RadioGroup } from "@design-system/Radio/Radio";
import { Switch } from "@design-system/Switch/Switch";
import { VStack } from "@design-system/VStack/VStack";
import { BoardCardTagExamples } from "./BoardCardTagExamples";
import { BoardPlaygroundCardFieldControls } from "./BoardPlaygroundCardFieldControls";
import {
    ADD_PLACEMENT_OPTIONS,
    AGENT_DRAWER_OPTIONS,
    type AgentDrawerMode,
    type BoardPlaygroundSettings,
    WORK_CARD_FIELD_OPTIONS,
} from "./boardPlaygroundSettings";
import { TASK_STAGE_COLUMNS } from "./taskBoardConfig";
import { TASK_CARD_FIELD_OPTIONS } from "./taskBoardView";
import styles from "./BoardPlayground.module.scss";

interface BoardPlaygroundControlsProps {
    id: string;
    settings: BoardPlaygroundSettings;
    /** Visible card fields, from the Display popover. */
    cardFieldIds: readonly string[];
    onSettingsChange: (settings: BoardPlaygroundSettings) => void;
}

const toPlacements = (values: readonly string[]) =>
    ADD_PLACEMENT_OPTIONS.flatMap(({ value }) =>
        values.includes(value) ? [value] : [],
    );

/**
 * Side panel for the board playground: the stress data set and agent
 * drawer, then for task cards which create buttons each column offers, the
 * move rules, the ordering mode, and where each visible card field sits on
 * the card and whether it edits inline; for Work cards, the card fields.
 */
export const BoardPlaygroundControls = ({
    id,
    settings,
    cardFieldIds,
    onSettingsChange,
}: Readonly<BoardPlaygroundControlsProps>) => {
    const isTasks = settings.cardType === "tasks";
    const update = (patch: Partial<BoardPlaygroundSettings>) =>
        onSettingsChange({ ...settings, ...patch });
    const visibleCardFields = TASK_CARD_FIELD_OPTIONS.filter((field) =>
        cardFieldIds.includes(field.id),
    );

    return (
        <aside id={id} aria-label="Board controls" className={styles.controls}>
            <VStack as="section" gap={12}>
                <h3 className={styles.controlsHeading}>Data</h3>
                <Switch
                    size="sm"
                    label={`Stress mode (1,000 ${isTasks ? "tasks" : "applications"})`}
                    checked={settings.isStressMode}
                    onCheckedChange={(isStressMode) => update({ isStressMode })}
                />
                <VStack gap={6}>
                    <span className={styles.controlsLabel}>Agent drawer</span>
                    <RadioGroup<AgentDrawerMode>
                        aria-label="Agent drawer"
                        value={settings.agentDrawer}
                        onValueChange={(agentDrawer) => update({ agentDrawer })}
                    >
                        {AGENT_DRAWER_OPTIONS.map(({ value, label }) => (
                            <Radio key={value} value={value} label={label} />
                        ))}
                    </RadioGroup>
                </VStack>
            </VStack>
            {isTasks ? (
                <>
                    <VStack as="section" gap={12}>
                        <h3 className={styles.controlsHeading}>
                            Create buttons in stage columns
                        </h3>
                        {TASK_STAGE_COLUMNS.map((column) => (
                            <VStack key={column.id} gap={6}>
                                <span className={styles.controlsLabel}>
                                    {column.title}
                                </span>
                                <CheckboxGroup
                                    aria-label={`Create buttons in ${column.title}`}
                                    value={[
                                        ...(settings.addPlacementsByColumn[
                                            column.id
                                        ] ?? []),
                                    ]}
                                    onValueChange={(values) =>
                                        update({
                                            addPlacementsByColumn: {
                                                ...settings.addPlacementsByColumn,
                                                [column.id]:
                                                    toPlacements(values),
                                            },
                                        })
                                    }
                                >
                                    {ADD_PLACEMENT_OPTIONS.map(
                                        ({ value, label }) => (
                                            <Checkbox
                                                key={value}
                                                name={value}
                                                label={label}
                                            />
                                        ),
                                    )}
                                </CheckboxGroup>
                            </VStack>
                        ))}
                    </VStack>
                    <VStack as="section" gap={12}>
                        <h3 className={styles.controlsHeading}>Move rules</h3>
                        <Checkbox
                            label="Lock canceled tasks"
                            checked={settings.isLockEnabled}
                            onCheckedChange={(isLockEnabled) =>
                                update({ isLockEnabled })
                            }
                        />
                        <Checkbox
                            label="Only done tasks move to Deliver"
                            checked={settings.isDeliverRuleEnabled}
                            onCheckedChange={(isDeliverRuleEnabled) =>
                                update({ isDeliverRuleEnabled })
                            }
                        />
                    </VStack>
                    <VStack as="section" gap={12}>
                        <h3 className={styles.controlsHeading}>Order</h3>
                        <Switch
                            size="sm"
                            label="Manual order"
                            checked={settings.isManualOrder}
                            onCheckedChange={(isManualOrder) =>
                                update({ isManualOrder })
                            }
                        />
                    </VStack>
                    <BoardPlaygroundCardFieldControls
                        visibleCardFields={visibleCardFields}
                        placements={settings.cardFieldPlacements}
                        editableFieldIds={settings.editableCardFieldIds}
                        onPlacementsChange={(cardFieldPlacements) =>
                            update({ cardFieldPlacements })
                        }
                        onEditableFieldIdsChange={(editableCardFieldIds) =>
                            update({ editableCardFieldIds })
                        }
                    />
                    <BoardCardTagExamples />
                </>
            ) : (
                <VStack as="section" gap={12}>
                    <h3 className={styles.controlsHeading}>Card fields</h3>
                    <CheckboxGroup
                        aria-label="Work card fields"
                        value={[...settings.workCardFieldIds]}
                        onValueChange={(values) =>
                            update({
                                workCardFieldIds:
                                    WORK_CARD_FIELD_OPTIONS.flatMap((field) =>
                                        values.includes(field.id)
                                            ? [field.id]
                                            : [],
                                    ),
                            })
                        }
                    >
                        {WORK_CARD_FIELD_OPTIONS.map((field) => (
                            <Checkbox
                                key={field.id}
                                name={field.id}
                                label={field.label}
                            />
                        ))}
                    </CheckboxGroup>
                </VStack>
            )}
        </aside>
    );
};
